// Порт ref RichTextEditor (edit-ветка). Read-only рендер живёт в RichTextView —
// это отдельный компонент именно потому, что useEditor строит tiptap Editor
// eagerly, а в edit mode блок всегда editable, так что setEditable-синхронизация
// больше не нужна.
import { useEffect, useId, useRef } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { cn } from '@teach/ui'
import {
  highlightScopeStyles,
  type HighlightTokens,
} from '@/features/editor/lib/designElementStyles'
import { useTextEditor } from './TextEditorContext'
import { WavyUnderline } from './wavyUnderline'
import { EmDash } from '@/lib/emDashExtension'
import { DesignTextLimit, SKIP_TEXT_LIMIT } from './designTextLimit'

interface Props {
  content: string
  onChange: (html: string) => void
  highlight: HighlightTokens
  textSizeClassName?: string
  textAlignClassName?: string
  textColor?: string
  onFocusChange?: (focused: boolean) => void
  /** Серая подсказка в пустом поле: новый абзац создаётся пустым, а не с текстом-заготовкой. */
  placeholder?: string
  /** Потолок длины (как меряет бэк — `designTextLength`): ввод и вставка сверх него не
   *  проходят. Иначе бэк отбивал блок целиком 422, и пропадала вся правка (kkc82). */
  maxLength?: number
}

// Глобальная подсказка tiptap (`src/index.css`) плавает влево и красится приглушённым
// цветом приложения — на холсте она обязана следовать выравниванию и цвету текста
// элемента (подложка меняет цвет). Скоуп удвоен, чтобы перебить глобальное правило.
const placeholderStyles = (scope: string): string =>
  `.${scope}.${scope} .tiptap p.is-editor-empty:first-child::before{float:none;display:block;color:currentColor;opacity:.5}`

export const RichTextEditor = ({
  content,
  onChange,
  highlight,
  textSizeClassName,
  textAlignClassName,
  textColor = 'inherit',
  onFocusChange,
  placeholder = '',
  maxLength,
}: Props) => {
  const { setActiveEditor } = useTextEditor()
  const scope = `rte-${useId().replace(/:/g, '')}`
  const settingContent = useRef(false)
  // Tracks whether THIS instance is the one the context currently points at —
  // read (not the context's `activeEditor`) so the unmount cleanup below
  // never needs a stale closure over context state to decide whether to clear it.
  const isActive = useRef(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        strike: false,
        code: false,
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        // tiptap v3 StarterKit bundles Underline itself; we register our own
        // instance below (kept explicit per the allowlist), so disable the
        // built-in copy here to avoid a duplicate-extension-name warning.
        underline: false,
        // v3 StarterKit also bundles Link (autolink/linkOnPaste/openOnClick on
        // by default) — `<a>` is outside the backend allowlist, so any
        // URL-shaped text would be wrapped in a tag/attrs that vanish on
        // save+reload. Disable it; the allowlist has no link mark at all.
        link: false,
      }),
      Highlight.configure({ multicolor: false }),
      Underline,
      WavyUnderline,
      // «--» → «—», как во всех полях студии (`lib/emDash.ts`).
      EmDash,
      Placeholder.configure({ placeholder }),
      ...(maxLength ? [DesignTextLimit.configure({ limit: maxLength })] : []),
    ],
    content: content || '',
    onUpdate: ({ editor: e }) => {
      // tiptap's getHTML() for an empty document is the truthy string
      // '<p></p>', not '' — writing that through would defeat the read-only
      // fallback (getLocalizedDisplay's pickLang() never sees an empty value
      // to fall back from). Normalise to '' so "cleared to leave
      // untranslated" reads back as genuinely empty.
      if (!settingContent.current) onChange(e.isEmpty ? '' : e.getHTML())
    },
    editorProps: {
      attributes: {
        // leading-[normal], не leading-tight: прямой класс на ProseMirror-элементе
        // побеждает унаследованный с корня канваса leading-[normal] всегда,
        // независимо от специфичности (правило на самом элементе против
        // наследования). useOverflowDetection меряет именно эту, editable-ветку —
        // с leading-tight (1.25) она мерила БОЛЕЕ ПЛОТНУЮ раскладку, чем увидит
        // ученик в read-only RichTextView, и предупреждение «переполнен» могло
        // не загореться там, где у ученика блок реально переполнится.
        class: cn('leading-[normal] outline-none', textSizeClassName, textAlignClassName),
        style: `color: ${textColor}; white-space: pre-wrap;`,
      },
    },
  })

  // Пока редактор в фокусе — не перезаписываем контент: setContent во время
  // набора съедал хвостовые пробелы и сбрасывал каретку (комментарий рефа).
  useEffect(() => {
    if (editor && !editor.isFocused && content !== editor.getHTML()) {
      settingContent.current = true
      // Текст с сервера ставим как есть, даже длиннее лимита: лимит — только на ввод автора.
      editor
        .chain()
        .setMeta(SKIP_TEXT_LIMIT, true)
        .setContent(content || '')
        .run()
      requestAnimationFrame(() => {
        settingContent.current = false
      })
    }
  }, [content, editor])

  // `setActiveEditor` is otherwise never cleared: not on blur, not on unmount,
  // not on block switch. That leaves the toolbar pointed at whichever editor
  // last had focus (bolding the wrong element once a second one is selected)
  // or, once its own component tree unmounts, at a destroyed tiptap instance
  // (the toolbar keeps showing its buttons instead of falling back to the
  // hint, and clicking them silently no-ops).
  useEffect(
    () => () => {
      if (isActive.current) setActiveEditor(null)
    },
    [setActiveEditor],
  )

  return (
    <div
      className={cn(scope, 'w-full rounded-lg', textAlignClassName)}
      onClick={(e) => e.stopPropagation()}
      // Пробел не отдаём родителям — иначе его перехватывают обработчики канваса.
      onKeyDown={(e) => {
        if (e.key === ' ') e.stopPropagation()
      }}
      onFocus={() => {
        isActive.current = true
        setActiveEditor(editor)
        onFocusChange?.(true)
      }}
      onBlur={(e) => {
        // A toolbar button's onMouseDown calls preventDefault, so clicking it
        // never blurs this editor in the first place — the command runs
        // against the still-focused, still-active editor. This only fires
        // when focus genuinely leaves (another element, another block).
        if (!e.currentTarget.contains(e.relatedTarget)) {
          isActive.current = false
          setActiveEditor(null)
          onFocusChange?.(false)
        }
      }}
    >
      <style>{highlightScopeStyles(scope, highlight) + placeholderStyles(scope)}</style>
      <EditorContent editor={editor} />
    </div>
  )
}
